<template>
    <!-- Fix-Runde nach dem ui-validator (K1, 2026-08-20): KEIN
         `inline-flex items-center` mehr. Als Flex-Kind wurde der Textspan
         blockifiziert; auf 768 px brach die Zeile darum um und der Stift
         rutschte 148 px nach rechts, senkrecht zwischen beide Zeilen. Im
         normalen Inline-Fluss laeuft der Knopf am Textende mit, in jeder
         Breite. -->
    <span>
        <span data-test="project-fee-mode">
            {{ $t("projects.feeCalculationMode") }}
            {{ $t("projects.feeCalculationModes." + feeCalculation.mode) }}
        </span>

        <!-- Der Korrekturweg erscheint NUR, wenn der Server ihn mitliefert
             (`options`/`updateLink` stehen ausschliesslich im
             Superadmin-Zweig von `ProjectShowResource`). Kein `v-show`, kein
             ausgegrauter Knopf: was nicht im Payload steht, existiert im
             Markup nicht. -->
        <button
            v-if="canCorrect"
            type="button"
            data-test="project-fee-mode-edit"
            class="ml-2 inline-block align-text-bottom text-minplan-darkbeige hover:text-minplan-darkgreybeige"
            :title="$t('projects.feeCalculationModeCorrect')"
            @click="request"
        >
            <PencilIcon class="h-4 w-4" />
        </button>

        <jet-dialog-modal :show="open" @close="cancel">
            <template #title>
                {{ $t("projects.feeCalculationModeCorrectTitle") }}
            </template>

            <template #content>
                <p class="text-sm text-gray-700">
                    {{ $t("projects.feeCalculationModeCorrectText") }}
                </p>

                <div v-if="canCorrect" class="mt-4 space-y-3">
                    <label
                        v-for="option in feeCalculation.options"
                        :key="option"
                        class="flex items-start gap-3 text-sm text-gray-700 cursor-pointer select-none"
                        :for="'project-fee-mode-' + option"
                    >
                        <input
                            :id="'project-fee-mode-' + option"
                            v-model="selected"
                            type="radio"
                            name="project_fee_calculation_mode"
                            class="mt-1"
                            :value="option"
                            :disabled="processing"
                        />
                        <span>
                            <span class="block font-medium">
                                {{ $t("projects.feeCalculationModes." + option) }}
                            </span>
                            <span class="block text-xs text-gray-500">
                                {{ $t("projects.feeCalculationModeHints." + option) }}
                            </span>
                        </span>
                    </label>
                </div>
            </template>

            <template #footer>
                <button
                    type="button"
                    class="inline-block uppercase font-semibold text-xs tracking-widest"
                    @click="cancel"
                >
                    {{ $t("actions.cancel") }}
                </button>

                <primary-button
                    class="ml-3"
                    :class="{ 'opacity-25': processing }"
                    :disabled="processing"
                    @click="confirm"
                >
                    {{ $t("actions.confirm") }}
                </primary-button>
            </template>
        </jet-dialog-modal>
    </span>
</template>

<script>
// Auftrag Luca, 2026-08-20 (Spec
// docs/superpowers/specs/2026-08-20-berechnungsmodus-auf-projektebene-design.md,
// Entscheide 11 und 12).
//
// Zwei Dinge in einer Komponente, mit Absicht:
//
//   ANZEIGEN — der Modus steht dort, wo Gebühr und Honorar stehen, und zwar
//   für JEDES Agenturmitglied. Auf dem Projekt ist er keine Einstellung mehr,
//   sondern eine Eigenschaft des Mandats, die erklärt, wie die Zahlen daneben
//   zustande kommen.
//
//   KORRIGIEREN — der Notausgang für den Superadmin. Er hängt allein daran,
//   ob der Server `options` und `updateLink` mitgeliefert hat; die schreibende
//   Route trägt zusätzlich `can:superadmin`, Ausblenden allein genügt nie.
//
// Bewusst ein LOKALER Dialog statt einer eigenen Inertia-Modal-Seite — Muster
// und Begründung von `FeeCalculationModeCard.vue`: ein zweiter
// Controller-Endpunkt plus Page nur für eine Rückfrage wäre Aufwand ohne
// Gegenwert, und der lokale Dialog ist mit Vitest prüfbar.
//
// Der Zustand lebt hier und nicht in `ProjectsHeader.vue`: der Header ist eine
// Referenz-Implementierung (CLAUDE.md) und bleibt zustandslos.
import JetDialogModal from "@/Jetstream/DialogModal";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import { PencilIcon } from "@heroicons/vue/outline";

export default {
    name: "ProjectFeeCalculationMode",
    components: { JetDialogModal, PrimaryButton, PencilIcon },
    props: {
        project: { type: Object, required: true },
        feeCalculation: { type: Object, required: true },
    },
    data() {
        return {
            open: false,
            // Der im Dialog gewählte, noch NICHT geschriebene Modus. Beim
            // Öffnen auf den gespeicherten gesetzt, damit der Dialog den
            // Ist-Zustand zeigt.
            selected: this.feeCalculation.mode,
            processing: false,
        };
    },
    computed: {
        canCorrect() {
            return Array.isArray(this.feeCalculation.options);
        },
    },
    methods: {
        request() {
            this.selected = this.feeCalculation.mode;
            this.open = true;
        },
        cancel() {
            this.open = false;
            this.selected = this.feeCalculation.mode;
        },
        confirm() {
            if (this.processing) {
                return;
            }

            // Unveränderte Bestätigung schreibt nichts — sonst stünde im
            // Protokoll ein Wechsel, der keiner war.
            if (this.selected === this.feeCalculation.mode) {
                this.open = false;

                return;
            }

            // Die Adresse wird VOR dem Sperren gebildet, und der Wurf wird
            // gefangen (Staging-Befund 2026-08-17, übernommen vom
            // Agentur-Zwilling): `route()` wirft, solange der rollenweise
            // Ziggy-Routen-Cache die neue Route noch nicht kennt. Stand
            // `processing = true` davor, blieb der Dialog offen und die
            // Schaltfläche gesperrt — ohne Meldung, ohne Anfrage.
            let url;

            try {
                url = this.route(this.feeCalculation.updateLink, this.project.uuid);
            } catch (error) {
                this.open = false;
                this.processing = false;
                this.$notify({
                    group: "error",
                    title: "actions.error",
                    text: "actions.defaultError",
                });

                return;
            }

            this.processing = true;
            this.$inertia.put(
                url,
                { mode: this.selected },
                {
                    preserveScroll: true,
                    onSuccess: () =>
                        this.$notify({
                            group: "success",
                            title: "actions.success",
                            text: "projects.feeCalculationModeUpdated",
                        }),
                    // Ohne diesen Zweig schlösse sich der Dialog bei einem
                    // Fehler wortlos (`onFinish` läuft in beiden Fällen) und
                    // der Modus stünde unverändert da — ununterscheidbar von
                    // «hat funktioniert».
                    onError: () =>
                        this.$notify({
                            group: "error",
                            title: "actions.error",
                            text: "actions.defaultError",
                        }),
                    onFinish: () => {
                        this.processing = false;
                        this.open = false;
                    },
                }
            );
        },
    },
};
</script>

<style scoped></style>
